// Client-side layer registry.
// Maps layer ids to lazy-loaded React components wrapped in their own ErrorBoundary.
// Each layer is independently lazy-loaded — a crash in one cannot affect another.
//
// Only add a layer here when its client module exists.
// The "personal" session type uses the existing ChatArea path — not a registered layer.
//
// Film Studio: receives chatArea as a ReactNode (from AppShell) so it can host ChatArea
// in its Workshop view without needing to re-import ChatArea's prop surface.

import { lazy, Suspense, type ReactNode } from 'react';
import { StoryboardErrorBoundary } from './storyboard/StoryboardErrorBoundary';
import { FilmStudioErrorBoundary } from './film-studio/FilmStudioErrorBoundary';
import { WritingRoomErrorBoundary } from './writing-room/WritingRoomErrorBoundary';
import type { Project, ProjectPatch } from '../auth/types';
import type { MutableRefObject } from 'react';
import type { WritingRoomDocumentUpdated } from './writing-room/WritingRoomLayer';
import type { RoomState } from './writing-room/room-state';
import type { WritingRoomVoice } from './writing-room/read-aloud';

export type ClientLayerId = 'storyboard-studio' | 'film-studio' | 'writing';

const LAYER_FALLBACK = (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      background: 'var(--bg-void)',
      color: 'var(--text-ghost)',
      fontFamily: 'var(--font-body)',
      fontSize: '0.75rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    }}
  >
    Loading Studio...
  </div>
);

// ─── Storyboard (legacy) ──────────────────────────────────────────────────────

const LazyStoryboard = lazy(() =>
  import('./storyboard/StoryboardLayer').then(m => ({ default: m.StoryboardLayer }))
);

/** Renders the Storyboard layer inside its own ErrorBoundary + Suspense. */
function StoryboardLayerEntry() {
  return (
    <StoryboardErrorBoundary>
      <Suspense fallback={LAYER_FALLBACK}>
        <LazyStoryboard />
      </Suspense>
    </StoryboardErrorBoundary>
  );
}

// ─── Film Studio ──────────────────────────────────────────────────────────────

const LazyFilmStudio = lazy(() =>
  import('./film-studio/FilmStudioLayer').then(m => ({ default: m.FilmStudioLayer }))
);

interface FilmStudioLayerEntryProps {
  chatArea: ReactNode;
}

const LazyWritingRoom = lazy(() =>
  import('./writing-room/WritingRoomLayer').then(m => ({ default: m.WritingRoomLayer })),
);

interface WritingRoomLayerEntryProps {
  project: Project | null;
  chatArea: ReactNode;
  connected: boolean;
  sendRawWs?: (payload: Record<string, unknown>) => void;
  documentUpdatedRef?: MutableRefObject<((event: WritingRoomDocumentUpdated) => void) | null>;
  /** WR-2 (ADR-018) getter-pattern seam — passed straight through. */
  roomStateRef?: MutableRefObject<(() => RoomState | null) | null>;
  onProjectPatched?: (patch: ProjectPatch) => void;
  projects?: Project[];
  activeSessionId?: string | null;
  creatorIdentity?: string | null;
  onAttachProject?: (project: Project) => Promise<boolean>;
  onCreateProject?: (name: string, identity: string) => Promise<Project | null>;
  /** WR-M: the phone top bar's route back to the app menu — the shell's own
   *  header is hidden while the writing layer is live at <=700px. */
  onOpenAppMenu?: () => void;
  /** AppShell's one-way, guarded sidebar collapse — see
   *  AppShell.tsx's `collapseSidebarForDocumentView` doc comment for the
   *  full "why one-way is safe" reasoning. This entry point has no opinion
   *  about it at all, same pass-through shape as onOpenAppMenu above. */
  onEnterDocumentView?: () => void;
  /** WR session-scoped documents — the active session's own document
   *  override (null = use the project's default), and the callback that
   *  applies a successful switch to the central sessions list. */
  sessionDocumentPath?: string | null;
  sessionDocumentType?: string | null;
  onSessionDocumentSwitched?: (documentPath: string, documentType: string) => void;
  /** Read-aloud slice 1 — optional, so every pre-existing caller of this
   *  entry point is unaffected; the Listen control simply doesn't render
   *  without it (see WritingRoomLayer.tsx's own `editor && voice` gate). */
  voice?: WritingRoomVoice;
  onNewChat?: () => void;
}

function WritingRoomLayerEntry({
  project, chatArea, connected, sendRawWs, documentUpdatedRef, roomStateRef, onProjectPatched,
  projects, activeSessionId, creatorIdentity, onAttachProject, onCreateProject, onOpenAppMenu,
  onEnterDocumentView, sessionDocumentPath, sessionDocumentType, onSessionDocumentSwitched, voice, onNewChat,
}: WritingRoomLayerEntryProps) {
  return (
    <WritingRoomErrorBoundary>
      <Suspense fallback={LAYER_FALLBACK}>
        <LazyWritingRoom
          project={project}
          chatArea={chatArea}
          connected={connected}
          sendRawWs={sendRawWs}
          documentUpdatedRef={documentUpdatedRef}
          roomStateRef={roomStateRef}
          onProjectPatched={onProjectPatched}
          projects={projects}
          activeSessionId={activeSessionId}
          creatorIdentity={creatorIdentity}
          onAttachProject={onAttachProject}
          onCreateProject={onCreateProject}
          onOpenAppMenu={onOpenAppMenu}
          onEnterDocumentView={onEnterDocumentView}
          sessionDocumentPath={sessionDocumentPath}
          sessionDocumentType={sessionDocumentType}
          onSessionDocumentSwitched={onSessionDocumentSwitched}
          voice={voice}
          onNewChat={onNewChat}
        />
      </Suspense>
    </WritingRoomErrorBoundary>
  );
}

/** Renders the Film Studio layer inside its own ErrorBoundary + Suspense. */
function FilmStudioLayerEntry({ chatArea }: FilmStudioLayerEntryProps) {
  return (
    <FilmStudioErrorBoundary>
      <Suspense fallback={LAYER_FALLBACK}>
        <LazyFilmStudio chatArea={chatArea} />
      </Suspense>
    </FilmStudioErrorBoundary>
  );
}

// ─── Registry ─────────────────────────────────────────────────────────────────

/** Render a client layer by id. Returns null for unknown ids.
 *
 * @param layerId  The layer id from the session record.
 * @param opts     Extra render options (chatArea for film-studio).
 */
export function renderClientLayer(
  layerId: string,
  opts?: {
    chatArea?: ReactNode;
    project?: Project | null;
    connected?: boolean;
    sendRawWs?: (payload: Record<string, unknown>) => void;
    documentUpdatedRef?: MutableRefObject<((event: WritingRoomDocumentUpdated) => void) | null>;
    roomStateRef?: MutableRefObject<(() => RoomState | null) | null>;
    onProjectPatched?: (patch: ProjectPatch) => void;
    projects?: Project[];
    activeSessionId?: string | null;
    creatorIdentity?: string | null;
    onAttachProject?: (project: Project) => Promise<boolean>;
    onCreateProject?: (name: string, identity: string) => Promise<Project | null>;
    onOpenAppMenu?: () => void;
    onEnterDocumentView?: () => void;
    sessionDocumentPath?: string | null;
    sessionDocumentType?: string | null;
    onSessionDocumentSwitched?: (documentPath: string, documentType: string) => void;
    voice?: WritingRoomVoice;
    onNewChat?: () => void;
  }
): React.ReactElement | null {
  // Legacy storyboard-studio sessions still render the storyboard layer.
  if (layerId === 'storyboard-studio') {
    return <StoryboardLayerEntry />;
  }
  if (layerId === 'film-studio' || layerId === 'film-editing') {
    return <FilmStudioLayerEntry chatArea={opts?.chatArea ?? null} />;
  }
  if (layerId === 'writing') {
    return (
      <WritingRoomLayerEntry
        project={opts?.project ?? null}
        chatArea={opts?.chatArea ?? null}
        connected={opts?.connected ?? false}
        sendRawWs={opts?.sendRawWs}
        documentUpdatedRef={opts?.documentUpdatedRef}
        roomStateRef={opts?.roomStateRef}
        onProjectPatched={opts?.onProjectPatched}
        projects={opts?.projects}
        activeSessionId={opts?.activeSessionId}
        creatorIdentity={opts?.creatorIdentity}
        onAttachProject={opts?.onAttachProject}
        onCreateProject={opts?.onCreateProject}
        onOpenAppMenu={opts?.onOpenAppMenu}
        onEnterDocumentView={opts?.onEnterDocumentView}
        sessionDocumentPath={opts?.sessionDocumentPath}
        sessionDocumentType={opts?.sessionDocumentType}
        onSessionDocumentSwitched={opts?.onSessionDocumentSwitched}
        voice={opts?.voice}
        onNewChat={opts?.onNewChat}
      />
    );
  }
  return null;
}
